import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@shared/auth';

/**
 * 重試上限。
 *
 * 三次是 TanStack Query 的預設值，這裡刻意降到兩次：這些 app 的請求全部打向同一個
 * BFF，真的連不上時多試一次只是讓「載入失敗」晚一秒出現，而使用者早就在看著轉圈。
 */
const MAX_RETRIES = 2;

/**
 * 資料在多久之內視為新鮮。
 *
 * 兩分鐘是「同一個人可能在手機與桌機之間換手」與「不要每次切回分頁就重打一輪」
 * 的折衷。過期只代表**下一次觸發時**會重抓（重新聚焦、重新掛載），不會自己在
 * 背景輪詢。
 *
 * ⚠️ **這個值不可以等於 Keycloak 的 access token 壽命**（`sre/terraform/keycloak.tf`
 * 的 `access_token_lifespan`）。兩者相同時，放置一段時間後切回 app，所有查詢會與
 * token 同時到期，併發請求同時觸發續期、撞上 refresh token rotation，
 * 症狀是間歇性地被要求重新登入。
 *
 * 後端已有單飛鎖擋住併發續期（auth-service 的 `SingleFlightAuthorizedClientManager`），
 * 這裡錯開週期是第二道防線。
 */
const STALE_TIME_MS = 2 * 60 * 1000;

/**
 * 建立 QueryClient。
 *
 * 做成工廠而非匯出單例：單例會在模組載入時就建好，測試之間也共用同一份快取，
 * 於是前一個測試留下的資料會漏進下一個。由呼叫端決定生命週期（app 建一個、
 * 測試每次建新的）。
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        retry: shouldRetry,
      },
      mutations: {
        /*
         * 寫入一律不重試。讀取重試最多只是多花一次往返，但重試一個可能已經送達的
         * POST 會多出一筆紀錄——而使用者看不到那筆是怎麼來的。失敗就回報，讓他
         * 自己決定要不要再送一次（表單內容都還在）。
         */
        retry: false,
      },
    },
  });
}

/**
 * 重試判準。
 *
 * **認證失敗絕不重試。**401／403 不是暫時性故障，重試只會把同一個錯誤再問
 * 兩次、把登入頁的出現往後延——而 `apiFetch` 收到 401 當下就已經通知 `useSession`
 * 換上登入頁了（見 @shared/auth 的 api.ts）。
 *
 * 其餘 4xx 同理：請求本身有問題，重送不會變對。只有 5xx 與網路層的失敗
 * （`fetch` 直接拋、沒有 status）值得再試。
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;

  if (error instanceof ApiError) {
    return error.status >= 500;
  }

  /* 非 ApiError 多半是網路不通或 CORS，值得再試一次 */
  return true;
}
